import * as React from "react";
import { useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Grid, GridColumn as Column, GridNoRecords } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import * as api from "api/index";
import styles from "../Settings.module.scss";
import SubmitButton from "components/common/SubmitButton";
import { GridLoader } from "components/GridLoader";
import AddEditNotificationProfile from "./AddEditNotificationProfile";

const CheckboxCell = (props) => (
  <td className={`${styles["checkbox"]} text-center`}>
    <Checkbox
      checked={!!props.dataItem[props.field]}
      onChange={(e) => {
        props.onChange({
          dataIndex: 0,
          dataItem: props.dataItem,
          field: props.field,
          syntheticEvent: e.syntheticEvent,
          value: e.target.value,
        });
      }}
    />
  </td>
);

const NotificationProfiles = () => {
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState([]);
  const [profileValue, setProfileValue] = useState();
  const [profileInfo, setProfileInfo] = useState();
  const [notificationItems, setNotificationItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInActive, setIsInactive] = useState(false);

  const [openForm, setOpenForm] = useState(false);
  const [type, setType] = useState("");
  const [editItem, setEditItem] = useState({});
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [isSubmittingAll, setIsSubmittingAll] = useState(false);

  const fetchProfiles = async () => {
    try {
      const res = await api.getNotificationProfiles();
      setProfileData(res?.data?.data ?? []);
    } catch (_) { }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.getNotificationProfilesItems();
      setNotificationItems(res?.data?.data ?? []);
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchProfiles();
    fetchItems();
  }, []);

  React.useEffect(() => {
    setIsInactive(profileInfo?.active === false);
  }, [profileInfo]);

  const updateItemsFromProfile = (profile) => {
    const accessCode = profile?.accessCode ?? 0;
    const emailAccessCode = profile?.emailAccessCode ?? 0;
    setNotificationItems((prev) =>
      prev.map((item) => ({
        ...item,
        allowAccess: (accessCode & item.value) === item.value,
        allowEmail: (emailAccessCode & item.value) === item.value,
      }))
    );
  };

  const onChangeProfile = async (e) => {
    if (e.value != null) {
      setProfileValue(e.value);
      try {
        const res = await api.getSpecificNotificationProfile(e.value.profileId);
        const data = res?.data?.data;
        setProfileInfo(data);
        updateItemsFromProfile(data);
      } catch (_) { }
    } else {
      setProfileValue({ profileName: "", value: null });
      setProfileInfo(null);
      updateItemsFromProfile({ accessCode: 0 });
    }
  };

  const handleItemChange = (e) => {
    setNotificationItems((prev) =>
      prev.map((item) =>
        item.name === e.dataItem.name
          ? { ...item, [e.field]: e.value }
          : item
      )
    );
  };

  const saveData = async () => {
    setIsSubmittingAll(true);
    try {
      let accessCode = 0;
      let emailAccessCode = 0;
      notificationItems.forEach((item) => {
        if (item.allowAccess) accessCode += item.value;
        if (item.allowEmail) emailAccessCode += item.value;
      });
      const data = {
        profileName: profileInfo?.profileName ?? profileValue.profileName,
        accessCode: accessCode,
        emailAccessCode: emailAccessCode,
        active: profileInfo?.active ?? profileValue.active ?? true,
      };
      await api.updateSpecificNotificationProfile(data, profileInfo?.profileId ?? profileValue.profileId);
      toast.success("Settings Updated");
    } catch (_) {
    } finally {
      setIsSubmittingAll(false);
    }
  };

  const handleSubmit = async (event) => {
    setIsSubmittingForm(true);
    try {
      if (type === "add") {
        await api.addNotificationProfile(event);
        toast.success("Profile Added");
        fetchProfiles();
      } else if (type === "edit") {
        await api.updateNotificationProfile(event, event.profileId);
        toast.success("Profile Updated");
        setProfileValue(event);
        fetchProfiles();
      } else if (type === "copy") {
        const data = {
          profileName: event.profileName,
          accessCode: profileInfo?.accessCode ?? 0,
          emailAccessCode: profileInfo?.emailAccessCode ?? 0,
          active: profileInfo?.active ?? true,
        };
        await api.addNotificationProfile(data);
        toast.success("Profile Added");
        fetchProfiles();
      }
      setOpenForm(false);
    } catch (_) {
    } finally {
      setIsSubmittingForm(false);
    }
  };

  const title = () => {
    if (type === "add") return "Add Profile";
    if (type === "edit") return "Edit Profile";
    return "Copy Profile";
  };

  const isProfileSelected =
    profileValue !== undefined && profileValue.value !== null;

  return (
    <div className="d-flex flex-column h-100 w-100">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-shrink-0 settings-container">
        <div className="d-flex align-items-center">
          <div className="d-flex flex-column">
            <Label className="label mb-2">Select Profile</Label>
            <ComboBox
              style={{ width: "350px" }}
              data={profileData}
              textField={"profileName"}
              iconClassName="wa"
              value={profileValue}
              onChange={onChangeProfile}
              placeholder={"Search Profile"}
            />
          </div>
          <div>
            <Label className="label ms-3 mt-4 pt-1 text-danger">
              {isInActive && "Inactive"}
            </Label>
          </div>
        </div>
        <div className="mt-4 d-flex flex-wrap gap-1 gap-xl-3">
          <Button
            className="common-btn setting-btn"
            disabled={!isProfileSelected}
            onClick={() => {
              setEditItem(profileInfo);
              setType("edit");
              setOpenForm(true);
            }}
          >
            Edit Profile
          </Button>
          <Button
            className="common-btn setting-btn"
            disabled={!isProfileSelected}
            onClick={() => {
              setType("copy");
              setOpenForm(true);
            }}
          >
            Copy Profile
          </Button>
          <Button
            className="common-btn setting-btn"
            onClick={() => {
              setType("add");
              setOpenForm(true);
            }}
          >
            Add Profile
          </Button>
        </div>
      </div>

      <div className="flex-grow-1 users-grid settings-container" style={{ minHeight: 0 }}>
        <Grid
          data={notificationItems}
          className="h-100"
          sortable={true}
          loader={
            <div style={{ height: "50px" }}>
              <GridLoader />
            </div>
          }
          showLoader={loading}
          onItemChange={handleItemChange}
        >
          <GridNoRecords>
            {loading ? (
              <span></span>
            ) : (
              notificationItems.length === 0 && "No records available"
            )}
          </GridNoRecords>
          <Column
            field="name"
            title="Name"
            editable={false}
            className="gridBoldField"
            width="220px"
          />
          <Column field="details" title="Details" editable={false} />
          <Column
            field="allowAccess"
            title="Notification"
            cell={CheckboxCell}
            width="115px"
          />
          <Column
            field="allowEmail"
            title="Email"
            cell={CheckboxCell}
            width="90px"
            headerClassName="grid-header-center"
          />
        </Grid>
      </div>

      <div
        className={`${styles["buttons-container"]} d-flex align-items-center justify-content-end gap-2 px-4 py-3`}
      >
        <Button
          className="common-btn bgBtnSecondary"
          onClick={() => navigate("/settings")}
        >
          Cancel
        </Button>
        <SubmitButton
          onClick={saveData}
          isLoading={isSubmittingAll}
          disabled={!isProfileSelected}
        >
          Save
        </SubmitButton>
      </div>

      {openForm && (
        <Dialog onClose={() => setOpenForm(false)} width={600} title={title()}>
          <AddEditNotificationProfile
            cancelEdit={() => setOpenForm(false)}
            onSubmit={handleSubmit}
            item={editItem}
            type={type}
            isSubmitting={isSubmittingForm}
          />
        </Dialog>
      )}
    </div>
  );
};

export default NotificationProfiles;
